import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { FeedbackBuffer } from '../feedback-buffer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum): the light in this piece is
// buried inside the volume and marched to; a rig outside it would be invisible.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'nebula',
  family: 'organic',
  danceability: 0.50,
  // 0, not 1. requiresTier gates SELECTION, not capability: this file has
  // had a real tier-0 layout all along (see its TIER table). The radio page
  // settles at tier 0 on the hardware it actually runs on - measured on
  // judicael.com - so at 1 this was never offered there at all, which is
  // why none of the recent visualizers ever appeared on the web radio. The
  // cost is that weak devices now get it at its tier-0 layout rather than
  // not at all; the frame-time governor still owns the tier itself.
  requiresTier: 0,
  energyLevel: 0.45,
  bpmMin: 50,
  bpmMax: 140,
  movement: 0.30,
  tonality: 0.90,
  prefers: { dynamics: [0.30, 1.0], percussiveness: [0.0, 0.55] },
  enabled: true
};

// Nebula: raymarched volumetric cloud, lit from INSIDE by drifting coloured
// sources, with scattering and self-shadowing. Refik Anadol's volumes, the
// clouds on Sphere - light born inside the matter, no surface anywhere.
//
// THE THREE THINGS THAT MAKE IT WORK, none of them optional:
//
//   TEMPORAL JITTER. Marching a volume in a fixed number of steps lays down
//   visible concentric shells. Offsetting each ray's start by a random
//   fraction of one step - varying per pixel AND per frame - turns that
//   banding into grain, which bloom and the filmic grain then bury. It is
//   what allows half the step count for no visible loss. ?nebjitter=0 shows
//   what it hides.
//
//   INTERNAL SHADOWING. A short march toward each light (6 steps) accumulating
//   the density crossed. Without it a raymarched cloud is a uniform mass with
//   no relief at all. ?nebshadow=0 shows it flat.
//
//   REDUCED RESOLUTION, ACCUMULATED. A cloud has no high frequency, so the
//   reduced buffer is invisible - as long as each frame is blended into a
//   history (which averages the jitter) and upscaled with a B-spline. Shown
//   raw, the jitter grain at an eighth of the screen is a field of flickering
//   blocks. See resolveFragmentShader.
//
// EVERYTHING IS MEDIUMP-SAFE BY CONSTRUCTION. On the television mediump is
// real fp16, where a value of magnitude M carries an ulp of M/1024 - so
// fract() of anything much over 16 has no fraction left and the usual
// hash idioms (sin*43758, or Hoskins' *0.1031 chain) collapse into flat
// bands. Every intermediate in hash() below stays under 9, and the drift is
// a bounded sine rather than an accumulating offset, so positions never grow.

// The cloud is meant to OCCUPY the frame, not sit in the middle of it. These
// are the half-extents before the aspect stretch below; x and z grow together
// so the silhouette stays wide from every angle the camera orbits through,
// and y is close behind so the volume reads as a mass rather than a band.
// WIDER THAN TALL, and not only past 16:9. The aspect stretch below only
// engages above 16:9, so on an ordinary screen the envelope was a sphere and
// the cloud could not reach the sides of the frame however big it grew - it
// sat as an upright oval with black margins either side. x and z together, so
// the orbit never turns the narrow axis towards the camera.
const SHAPE_HALF = [5.0, 3.6, 5.0];
// The envelope is NOT an ellipsoid. Three cheap sines push its radius in and
// out, so the silhouette has arms and hollows. A smooth ellipsoid with the
// noise carving two or three fat lobes inside it is what read as a pair of
// lungs, and no amount of density tuning fixes that: the outline itself has
// to be irregular.
const SHAPE_WOBBLE = 0.26;
const SHAPE_WOBBLE_DRIFT = 0.031;   // rad/s, so the arms move
// THE CLOUD'S EXTENT NOW VARIES, which it never did before: uShapeHalf was
// written once per aspect and nothing else touched the envelope, so the only
// things that changed size were the structures inside a fixed ball. This is
// the plateau radius - below it the cloud is at full density, above it the
// density falls to the envelope. Near 0 the cloud reaches its whole envelope
// and reads large and open; near 0.5 only the core is dense and it reads as a
// compact knot. It is the envelope that breathes, not a scale factor, so the
// march bounds and the camera distance are untouched.
//
// Note which way round this goes, because it is the opposite of what the name
// suggests at first glance: a LOW plateau radius means the density starts
// falling off almost at the centre, so the cloud is small and wispy; a HIGH
// one keeps it at full density most of the way out, so it is a large full
// mass. The ceiling is 0.68 rather than higher because the falloff band is
// what is left above it, and a narrow band is exactly what draws a visible
// edge between cloud and background.
// THE PLATEAU RUNS OPPOSITE TO THE SCALE, and that is the whole character of
// the breath: wide open means a large HAZE with structure in it, and drawn in
// means a small dense KNOT. Tying both to openness the same way made the big
// state a solid uniform egg - full density out to 68% of a large envelope is
// a ball, not a nebula.
const SHAPE_INNER_HAZE = 0.05;
const SHAPE_INNER_KNOT = 0.60;
const SHAPE_PERIOD_S = 41.0;        // incommensurable with the drift periods
// AND the envelope itself scales. The plateau radius alone only changes how
// FULL the cloud is - measured, it moved the effective radius by 7% while the
// silhouette stayed the same size, because the envelope's outer edge never
// moved. This is what actually changes the size. The march BOUND stays at the
// maximum, so the camera solve is untouched and the only cost of a small
// cloud is that the march crosses some empty space to reach it.
const SHAPE_SCALE_MIN = 0.56;
const SHAPE_SCALE_MAX = 1.12;
// A domain warp on the density's sample point. Cheap, and it is what folds
// the noise's fat lobes into arms and filaments.
const SHAPE_WARP = 1.15;
const BOUND_MARGIN = 1.03;   // the march envelope, an ellipsoid around the above
const CAMERA_RADIUS = 7.6;     // stays outside the stretched envelope (max 6.5)
const CAMERA_HEIGHT = 0.5;
const ORBIT_PERIOD_S = 180;

// MEASURED, not guessed. fbm here is a sum of value-noise octaves whose
// amplitudes total 0.9375, but reaching that total would need every octave to
// peak at the same point: over 40000 samples inside the ellipsoid the real
// maximum is 0.718, the mean 0.381 and the 99.9th percentile 0.699. The old
// 0.46 was picked as if the range were [0, 0.9375] and was in fact the 70th
// percentile, with the top of the ramp (0.71) above the 99.97th - so full
// density was unreachable BY CONSTRUCTION and the cloud could only ever be a
// haze: 2.9% of the frame covered, mean accumulated alpha 1.2%. Measured on
// GPU across the sweep: 0.46 -> 2.9% cover, 0.34 -> 8.7%, 0.28 -> 19.9%,
// 0.22 -> 42.4%.
const THRESHOLD_BASE = 0.30;
// The ramp is narrower than the old 0.25 as well: sitting near the middle of
// the distribution rather than in its tail, a wide ramp would wash the whole
// volume to the same mid density and flatten the internal structure.
const THRESHOLD_WIDTH = 0.18;
const THRESHOLD_SWING = 0.06;   // per phrase
const DENSITY_BASE = 1.25;
const DENSITY_BAND = 0.20;      // bands.low, +-20%
const LIGHT_BAND = 0.25;        // bands.high, +-25%
const PHRASE_LERP_BEATS = 2;
const SWEEP_S = 1.6;            // the drop sweep - the only fast thing here

const TIER = {
  // Tier 0 exists only as a live-downgrade refuge - the orchestrator never
  // offers nebula below tier 1 (requiresTier).
  // Steps measured on the Pi, not guessed: at 28 the tier-1 frame was 35.9ms
  // (27.9fps) against 26.4ms at 16. The raymarch is the only term that scales
  // with this - the bloom composer under it is a fixed 10.9ms floor - so this
  // is the lever the brief asks for first. The temporal jitter turns the lost
  // steps into grain rather than shells, which is why it can be this low.
  //
  // 2026-09-29, with the temporal accumulation: "la nebuleuse pixelise de
  // temps en temps et devient grossiere". Tier 2 cost 27 ms for the volume
  // alone at 1080p on the Mac, so the governor dropped it to tier 1 - whose
  // 0.14 target, jittered raw and stretched eight times, is a field of
  // flickering blocks - and raised it again 30 s later, over and over. With
  // the history averaging the jitter, tier 2 needs 20 steps, not 48: 5.5 ms,
  // indistinguishable from the old one side by side. The Pi cannot afford
  // more pixels (0.17 at tier 1 cost 24.8 ms against 17), so tiers 0 and 1
  // keep their resolution and trade steps for the accumulation and the
  // B-spline upscale: 16.0 ms at tier 1 against 16.4 before, and smooth.
  0: { scale: 0.12, steps: 6, octaves: 3, lights: 2, shadowSteps: 2 },
  1: { scale: 0.14, steps: 8, octaves: 3, lights: 2, shadowSteps: 2 },
  2: { scale: 0.36, steps: 20, octaves: 4, lights: 3, shadowSteps: 3 }
};
const DEFAULT_TIER = 2;
// The history's time constant, in SECONDS, not a per-frame weight: a fixed
// 0.3 per frame averaged over about six frames, which at 60 fps is 0.1 s but
// on a device drawing 8 frames a second is most of a second - and the stars
// drew comet tails behind the orbit (seen in the harness, whose headless
// frames come slowly). 50 ms is a weight of 0.28 at 60 fps and 0.64 at 10.
// The drop sweep is the only fast motion and gets a shorter one.
const ACCUM_TAU_S = 0.05;
const ACCUM_TAU_SWEEP_S = 0.02;
const MAX_STEPS = 64;   // upper sanity bound for ?nebsteps only; the raymarch
                        // loop is unrolled to each tier's own steps value.

// Compiled volume programs, keyed by the layout that shapes their source and
// deliberately never released - see the comment where they are created.
const VOLUME_MATERIALS = new Map();

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function lerp(a, b, t) { return a + (b - a) * t; }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

const quadVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

function buildVolumeShader(octaves, lights, shadowSteps, maxSteps) {
  // The fbm octaves are unrolled: the noise is the most-called function in
  // the whole program and a loop around it costs more than the code it saves.
  let fbmBody = '';
  let ampSum = 0;
  let amp = 0.5, freq = 1.0;
  for (let i = 0; i < octaves; i++) {
    ampSum += amp;
    fbmBody += `  v += ${amp.toFixed(4)} * vnoise(p * ${freq.toFixed(3)});\n`;
    amp *= 0.5; freq *= 2.03;   // 2.03, not 2: an exact doubling lines the
                                // octaves' lattices up and prints a grid
  }
  // A REAL loop, not one generated block per light. Each block inlines the
  // whole shadow march, and on the Pi's driver compile time tracks the size
  // of this program almost exactly: measured 1.8s at zero lights, 4.6s at
  // one, 12.7s at two - while the shadow march's own step count moved it not
  // at all (13.3s at 1 step vs 12.7s at 4), which is what says that loop is
  // NOT being unrolled and this one was only unrolled because I wrote it out.
  return `
  precision mediump float;

  uniform vec3 uCamPos;
  uniform vec3 uCamRight;
  uniform vec3 uCamUp;
  uniform vec3 uCamFwd;
  uniform float uTanHalf;
  uniform float uAspect;
  uniform float uTime;
  uniform float uFrame;
  uniform vec3 uDrift;
  uniform vec3 uShapeHalf;
  uniform vec3 uBoundHalf;
  uniform float uThreshold;
  uniform float uShapeInner;
  uniform float uShapeScale;
  uniform float uShapeSeed;
  uniform float uDensityScale;
  uniform vec3 uLightPos[3];
  uniform vec3 uLightColor[3];
  uniform float uLightCount;
  uniform float uLightReach;
  uniform float uShadowReach;
  uniform float uShadowCut;
  uniform vec3 uAmbient;
  uniform float uSteps;
  uniform float uJitterOn;
  uniform float uShadowOn;
  uniform float uDensityDebug;
  uniform vec3 uBgTint;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  uniform float uSafeAspect;

  varying vec2 vUv;

  // Every intermediate stays under 9 - see the header on fp16.
  float hash(vec3 p) {
    vec3 q = fract(p * vec3(0.1031, 0.1107, 0.0973));
    q = fract(q * (q.yzx + 5.3));
    q = fract(q * (q.zxy + 7.1));
    return fract(q.x + q.y * 0.37 + q.z * 0.11);
  }

  // Value noise, 8 taps.
  float vnoise(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    f = f * f * (3.0 - 2.0 * f);
    float n000 = hash(i);
    float n100 = hash(i + vec3(1.0, 0.0, 0.0));
    float n010 = hash(i + vec3(0.0, 1.0, 0.0));
    float n110 = hash(i + vec3(1.0, 1.0, 0.0));
    float n001 = hash(i + vec3(0.0, 0.0, 1.0));
    float n101 = hash(i + vec3(1.0, 0.0, 1.0));
    float n011 = hash(i + vec3(0.0, 1.0, 1.0));
    float n111 = hash(i + vec3(1.0, 1.0, 1.0));
    return mix(mix(mix(n000, n100, f.x), mix(n010, n110, f.x), f.y),
               mix(mix(n001, n101, f.x), mix(n011, n111, f.x), f.y), f.z);
  }

  float fbm(vec3 p) {
    float v = 0.0;
${fbmBody}
    return v;
  }

  // Soft confinement. Without it the cloud fills the frame and stops being a
  // cloud; the falloff is wide so there is never an edge.
  //
  // NOT an ellipsoid: the radius is pushed in and out by three sines, so the
  // outline has arms. And the plateau radius is a uniform that breathes (see
  // SHAPE_INNER_BIG), which is what makes the cloud change SIZE rather than
  // only changing its internal structure inside a fixed ball.
  float shapeAt(vec3 p) {
    float r = length(p / (uShapeHalf * uShapeScale));
    float w = sin(p.x * 0.55 + uShapeSeed)
            * sin(p.z * 0.47 - uShapeSeed * 1.31)
            + 0.55 * sin(p.y * 0.61 + uShapeSeed * 0.73);
    r *= 1.0 + ${SHAPE_WOBBLE.toFixed(3)} * w;
    return 1.0 - smoothstep(uShapeInner, 1.0, r);
  }

  // The density's sample point, warped. Used by density() ONLY, for the same
  // reason densityShadow() runs one octave instead of three: it is emitted
  // maxSteps x lights x shadowSteps times, and putting three more sines in
  // there cost 12% of the frame on the Pi (19.7 -> 22.2 ms at tier 1) for a
  // difference no one can see. A volumetric shadow is soft by nature and the
  // shadow field is already an approximation of the density field.
  vec3 cloudPoint(vec3 p) {
    vec3 q = p + uDrift;
    return q + ${SHAPE_WARP.toFixed(2)} * vec3(sin(q.z * 0.63),
                                               sin(q.x * 0.71),
                                               sin(q.y * 0.55));
  }

  float density(vec3 p) {
    vec3 q = cloudPoint(p);
    float d = fbm(q * 0.6);
    // Still a WIDE-ish smoothstep: this is the one place a hard edge could
    // appear, and a narrow band here is exactly what draws a visible boundary
    // between cloud and background.
    d = smoothstep(uThreshold, uThreshold + ${THRESHOLD_WIDTH.toFixed(2)}, d);
    d *= shapeAt(p);
    return d * uDensityScale;
  }

  // One octave, not ${octaves}. This is the density the shadow march samples,
  // and it is the single term the compiler emits the most times over:
  // maxSteps x lights x shadowSteps = ${maxSteps * lights * shadowSteps} copies
  // against ${maxSteps} for density() itself. A volumetric shadow is soft by
  // nature, so the octaves it loses never reach the image; what they cost is
  // shader compile time, which on the Pi's driver is seconds, not milliseconds.
  // The vnoise range [0,1] is scaled to fbm's [0,${ampSum.toFixed(4)}] so the
  // same uThreshold keeps its meaning.
  float densityShadow(vec3 p) {
    float d = vnoise((p + uDrift) * 0.6) * ${ampSum.toFixed(4)};
    d = smoothstep(uThreshold, uThreshold + ${THRESHOLD_WIDTH.toFixed(2)}, d);
    return d * shapeAt(p) * uDensityScale;
  }

  // The internal shadow: a SHORT march toward the light accumulating the
  // density crossed. Six steps is enough because a volumetric shadow is soft
  // by nature, and the cost here is multiplicative with everything else.
  float lightMarch(vec3 p, vec3 dir, float distToLight) {
    // Reach scaled to the cloud, not a fixed 1.6: in a volume stretched to
    // 6.5 units a march that only ever looked 1.6 ahead sampled almost nothing
    // and the relief it is here to produce fell to +4% of local gradient,
    // against +22% at the old size.
    float stepLen = min(distToLight, uShadowReach) / ${shadowSteps}.0;
    float acc = 0.0;
    vec3 q = p + dir * stepLen * 0.5;
    for (int i = 0; i < ${shadowSteps}; i++) {
      acc += densityShadow(q) * stepLen;
      q += dir * stepLen;
    }
    // 1.3, not the original 2.2. Tuned when the cloud was thin; on the dense
    // one it drove the core to black and split the nebula into two lit limbs
    // with a dark slot down the middle. Lower absorption keeps the relief
    // (measured +35% local gradient against shadows off) without extinguishing
    // the middle.
    return exp(-acc * 1.3);
  }

  // Takes the alpha accumulated SO FAR. Past uShadowCut the ray is already
  // mostly opaque and everything behind contributes a sliver, so the shadow
  // march - by far the most expensive thing per step - stops being worth its
  // price. Cheap when the cloud is dense, which is exactly when it is needed:
  // a dense volume used to run every one of those marches at every step.
  vec3 lightAt(vec3 p, float acc) {
    vec3 c = uAmbient;
    for (int i = 0; i < ${lights}; i++) {
      if (float(i) >= uLightCount) break;
      vec3 toL = uLightPos[i] - p;
      float dist = max(length(toL), 0.001);
      // Attenuation measured in CLOUD radii, not world units. A fixed
      // 1/(1+0.6d^2) was tuned for a 3-unit cloud: stretched to 6.5 it left
      // the outer nine tenths of the volume at 6% of the light and the image
      // came out at 14.5% coverage against 26% of actual density - the mass
      // was there and simply unlit.
      float dd = dist / uLightReach;
      float att = 1.0 / (1.0 + dd * dd * 1.4);
      float sh = (uShadowOn > 0.5 && acc < uShadowCut) ? lightMarch(p, toL / dist, dist) : 1.0;
      c += uLightColor[i] * (att * sh);
    }
    return c;
  }

  // Fine fixed stars, seen THROUGH the thin parts of the volume - the proof
  // that it is translucent rather than opaque.
  vec3 background(vec3 rd) {
    vec3 s = rd * 40.0;
    vec3 cell = floor(s);
    float h = hash(cell);
    float star = 0.0;
    if (h > 0.985) {
      vec3 f = fract(s) - 0.5;
      star = smoothstep(0.30, 0.0, length(f)) * (0.35 + h * 0.65);
    }
    float g = 1.0 - clamp(length(rd.xy) * 0.8, 0.0, 1.0);
    return uBgTint * (g * g * 0.05) + vec3(star);
  }

  void main() {
    vec2 ndc = vUv * 2.0 - 1.0;
    vec3 rd = normalize(uCamFwd + uCamRight * (ndc.x * uTanHalf * uAspect)
                                 + uCamUp * (ndc.y * uTanHalf));

    // Analytic entry/exit on the bounding sphere: the march never spends a
    // single step in empty space.
    // Entry/exit on the confinement ELLIPSOID, not a sphere around it: once
    // the cloud is stretched to a 3.2:1 frame a sphere large enough to hold it
    // is also large enough to swallow the camera, and the march would start at
    // the lens and spend most of its steps crossing vacuum.
    vec3 ro = uCamPos / uBoundHalf;
    vec3 rr = rd / uBoundHalf;
    float ea = dot(rr, rr);
    float b = dot(ro, rr);
    float disc = b * b - ea * (dot(ro, ro) - 1.0);

    vec3 col = vec3(0.0);
    float alpha = 0.0;

    if (disc > 0.0) {
      float sq = sqrt(disc);
      float t0 = max((-b - sq) / ea, 0.0);
      float t1 = (-b + sq) / ea;
      float stepLen = (t1 - t0) / uSteps;

      // TEMPORAL JITTER - per pixel and per frame. Without it the fixed step
      // count prints concentric shells; with it they become grain.
      float j = uJitterOn > 0.5 ? hash(vec3(gl_FragCoord.xy, uFrame)) : 0.0;
      float t = t0 + stepLen * j;

      // Bound = this tier's own step count, not the global maximum. GLSL ES
      // 1.00 needs a constant here, so the compiler unrolls it: a global 64
      // made tier 1 emit 64 copies of a body it only ever runs 28 of.
      for (int i = 0; i < ${maxSteps}; i++) {
        if (float(i) >= uSteps || alpha > 0.92) break;
        vec3 pos = uCamPos + rd * t;
        float d = density(pos);
        if (d > 0.002) {
          float a = d * stepLen;
          a = clamp(a, 0.0, 1.0);
          col += (1.0 - alpha) * a * lightAt(pos, alpha);
          alpha += (1.0 - alpha) * a;
        }
        t += stepLen;
      }
    }

    if (uDensityDebug > 0.5) {
      gl_FragColor = vec4(vec3(alpha), 1.0);
      return;
    }

    // Front-to-back compositing over the star field: the background shows
    // through wherever alpha stayed low.
    vec3 outCol = col + (1.0 - alpha) * background(rd);

    // Safe zone on the ACCUMULATED colour, floor 0.45 - the volume stays
    // visible behind the cover, merely subdued.
    // NO safe-zone attenuation. The dimmed patch behind the artwork read as a
    // black rectangle sitting in the middle of the cloud, and the cloud is
    // soft and dark enough at its core that the overlay stays legible over it.
    // Restoring it means a circular mix() on uSafeCenter/uSafeHalfSize here.


    // NO dither here: this target is a fraction of the screen, and a dither
    // pattern drawn at 152x48 and stretched eight times is a pattern of
    // blocks. The display pass dithers at full resolution.
    gl_FragColor = vec4(max(outCol, vec3(0.0)), 1.0);
  }
`;
}

// TEMPORAL ACCUMULATION. The ray start is jittered per pixel AND per frame,
// which trades the step shells for grain - and at the low resolutions this
// runs at, one grain is an 8x8 block of the screen flickering every frame:
// what read as "the nebula pixelates and turns coarse". Blending each frame
// into a running history averages the jitter instead of showing it, so the
// grain goes and the steps it stood in for can be halved. The camera orbits
// once in three minutes and the drift is slower still, so there is no trail;
// only the drop sweep moves fast, and it gets a stronger weight.
//
// NEIGHBOURHOOD CLAMP. The history has no reprojection, so anything that
// moves leaves a trail - invisible on a soft cloud, a comet tail on every
// star as the camera orbits. The history is therefore clamped to the range of
// the current frame's 3x3 neighbourhood first: where a star has moved on, its
// old brightness is outside that range and is cut; the cloud's slow changes
// stay inside it and keep their smoothing. At this resolution the nine taps
// cost nothing (drawing the stars at full resolution instead cost the Pi
// 3.4 ms).
const resolveFragmentShader = `
  precision mediump float;
  uniform sampler2D uCur;
  uniform sampler2D uPrev;
  uniform vec2 uTexel;
  uniform float uWeight;      // weight of the NEW frame
  varying vec2 vUv;
  void main() {
    vec3 c = texture2D(uCur, vUv).rgb;
    vec3 mn = c, mx = c;
    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec3 q = texture2D(uCur, vUv + vec2(float(i), float(j)) * uTexel).rgb;
        mn = min(mn, q); mx = max(mx, q);
      }
    }
    vec3 h = clamp(texture2D(uPrev, vUv).rgb, mn, mx);
    gl_FragColor = vec4(mix(h, c, uWeight), 1.0);
  }
`;

// Upscale with a cubic B-spline in four bilinear taps (Sigg and Hadwiger)
// instead of one bilinear one: bilinear from a target an eighth of the screen
// prints its lattice as soft squares. The cloud has no high frequency to lose.
// The dither lives here, at full resolution.
const upscaleFragmentShader = `
  precision highp float;
  uniform sampler2D uTex;
  uniform vec2 uTexSize;
  varying vec2 vUv;
  void main() {
    vec2 st = vUv * uTexSize - 0.5;
    vec2 i = floor(st);
    vec2 f = st - i;
    vec2 f2 = f * f, f3 = f2 * f;
    vec2 w0 = (1.0 - 3.0 * f + 3.0 * f2 - f3) / 6.0;
    vec2 w1 = (4.0 - 6.0 * f2 + 3.0 * f3) / 6.0;
    vec2 w2 = (1.0 + 3.0 * f + 3.0 * f2 - 3.0 * f3) / 6.0;
    vec2 w3 = f3 / 6.0;
    vec2 g0 = w0 + w1, g1 = w2 + w3;
    vec2 h0 = (w1 / g0 - 1.0 + i + 0.5) / uTexSize;
    vec2 h1 = (w3 / g1 + 1.0 + i + 0.5) / uTexSize;
    vec3 c = (texture2D(uTex, vec2(h0.x, h0.y)).rgb * g0.x + texture2D(uTex, vec2(h1.x, h0.y)).rgb * g1.x) * g0.y
           + (texture2D(uTex, vec2(h0.x, h1.y)).rgb * g0.x + texture2D(uTex, vec2(h1.x, h1.y)).rgb * g1.x) * g1.y;
    vec2 fcw = mod(floor(gl_FragCoord.xy), 64.0);
    float dith = (fract(dot(fcw, vec2(0.7548776662, 0.5698402909))) - 0.5) / 255.0;
    gl_FragColor = vec4(max(c + dith, vec3(0.0)), 1.0);
  }
`;

export class NebulaVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER[tier] ? tier : DEFAULT_TIER;
    const st = parseInt(urlParam('nebsteps'), 10);
    this._forcedSteps = Number.isFinite(st) && st > 1 && st <= MAX_STEPS ? st : null;
    this._jitter = urlParam('nebjitter') !== '0';
    // ?nebaccum=0 shows each frame raw, jitter grain and all.
    this._accumulate = urlParam('nebaccum') !== '0';
    this._shadow = urlParam('nebshadow') !== '0';
    this._densityDebug = urlParam('nebdensity') === '1' ? 1 : 0;
    // Shader-SIZE overrides, unlike ?nebshadow which only gates the march at
    // run time. The Pi's driver compiles this program in time proportional to
    // the raymarch body, and lights x shadowSteps multiplies the largest term
    // in it, so these are the two knobs that move compile time at all.
    const nl = parseInt(urlParam('neblights'), 10);
    this._forcedLights = Number.isFinite(nl) && nl >= 0 && nl <= 3 ? nl : null;
    const ns = parseInt(urlParam('nebshadowsteps'), 10);
    this._forcedShadowSteps = Number.isFinite(ns) && ns >= 1 && ns <= 8 ? ns : null;
    if (options && options.lights != null) this._forcedLights = options.lights;
    if (options && options.shadowSteps != null) this._forcedShadowSteps = options.shadowSteps;
    this._layoutOverride = options && options.layout ? options.layout : null;   // the bench's knob
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    const base = TIER[this._tier];
    const layout = Object.assign({}, base, this._layoutOverride || {}, {
      lights: this._forcedLights != null ? this._forcedLights : (this._layoutOverride && this._layoutOverride.lights) || base.lights,
      shadowSteps: this._forcedShadowSteps != null ? this._forcedShadowSteps : (this._layoutOverride && this._layoutOverride.shadowSteps) || base.shadowSteps
    });
    this._layout = layout;

    // One reduced-resolution target - no history needed, so the pair's second
    // allocation is skipped (see FeedbackBuffer's `single`).
    this._buffer = new FeedbackBuffer({ scale: layout.scale, single: true });
    // The history: a pair at the same size, ping-ponged by the resolve pass.
    this._history = new FeedbackBuffer({ scale: layout.scale });
    this._historyFresh = true;

    this._lightPos = [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()];
    // Base colours come from the palette; the ones handed to the shader carry
    // the bands.high gain on top, so the palette is never destroyed by it.
    this._lightBase = [new THREE.Color(0x6fb0ff), new THREE.Color(0xff7ab0), new THREE.Color(0xffd08a)];
    this._lightColor = [new THREE.Color(), new THREE.Color(), new THREE.Color()];

    this._uniforms = {
      uCamPos: { value: new THREE.Vector3() },
      uCamRight: { value: new THREE.Vector3(1, 0, 0) },
      uCamUp: { value: new THREE.Vector3(0, 1, 0) },
      uCamFwd: { value: new THREE.Vector3(0, 0, -1) },
      uTanHalf: { value: Math.tan((camera.fov || 68) * Math.PI / 360) },
      uAspect: { value: 16 / 9 },
      uTime: { value: 0 },
      uFrame: { value: 0 },
      uDrift: { value: new THREE.Vector3() },
      uShapeHalf: { value: new THREE.Vector3(SHAPE_HALF[0], SHAPE_HALF[1], SHAPE_HALF[2]) },
      uBoundHalf: { value: new THREE.Vector3(SHAPE_HALF[0], SHAPE_HALF[1], SHAPE_HALF[2]) },
      uShapeInner: { value: (SHAPE_INNER_HAZE + SHAPE_INNER_KNOT) * 0.5 },
      uShapeScale: { value: (SHAPE_SCALE_MIN + SHAPE_SCALE_MAX) * 0.5 },
      uShapeSeed: { value: 0 },
      uThreshold: { value: THRESHOLD_BASE },
      uDensityScale: { value: DENSITY_BASE },
      uLightPos: { value: this._lightPos },
      uLightColor: { value: this._lightColor },
      uLightCount: { value: layout.lights },
      uLightReach: { value: 2.0 },
      uShadowReach: { value: 1.6 },
      uShadowCut: { value: 0.45 },
      uAmbient: { value: new THREE.Color(0x0a1020) },
      // Clamped to the tier's own bound: the raymarch loop is unrolled to
      // exactly layout.steps, so asking for more would silently do nothing.
      uSteps: { value: Math.min(this._forcedSteps != null ? this._forcedSteps : layout.steps, layout.steps) },
      uJitterOn: { value: this._jitter ? 1 : 0 },
      uShadowOn: { value: this._shadow ? 1 : 0 },
      uDensityDebug: { value: this._densityDebug },
      uBgTint: { value: new THREE.Color(0x203050) },
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather,
      uSafeAspect: safeZoneUniforms.uAspect
    };

    this._volScene = new THREE.Scene();
    // The volume material OUTLIVES the visualizer, on purpose. Measured on the
    // Pi: this program takes 4.7s to compile, and three.js frees a program the
    // moment its last material is disposed - so every return of nebula in the
    // rotation froze the whole page for 4.7s again, not just the first one
    // (measured 4681/4640/4762ms over three attach-dispose cycles). The shader
    // source is a pure function of the tier layout, so one material per layout
    // is all there can ever be; keeping it costs one program and its uniforms.
    const key = `${layout.steps}:${layout.octaves}:${layout.lights}:${layout.shadowSteps}`;
    let mat = VOLUME_MATERIALS.get(key);
    if (mat) {
      // Adopt the cached uniform objects, carrying this instance's fresh
      // starting values into them - the previous instance left its own there.
      const mine = this._uniforms;
      for (const k in mine) if (mat.uniforms[k]) mat.uniforms[k].value = mine[k].value;
      this._uniforms = mat.uniforms;
    } else {
      mat = new THREE.ShaderMaterial({
        uniforms: this._uniforms,
        vertexShader: quadVertexShader,
        fragmentShader: buildVolumeShader(layout.octaves, layout.lights, layout.shadowSteps, layout.steps),
        depthTest: false, depthWrite: false
      });
      VOLUME_MATERIALS.set(key, mat);
    }
    this._volQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), mat);
    this._volQuad.frustumCulled = false;
    this._volScene.add(this._volQuad);
    this._ortho = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0, 10);
    this._ortho.position.z = 1;

    this._resolveUniforms = { uCur: { value: null }, uPrev: { value: null }, uWeight: { value: 1 }, uTexel: { value: new THREE.Vector2(1, 1) } };
    this._resolveScene = new THREE.Scene();
    this._resolveQuad = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._resolveUniforms, vertexShader: quadVertexShader, fragmentShader: resolveFragmentShader,
      depthTest: false, depthWrite: false
    }));
    this._resolveQuad.frustumCulled = false;
    this._resolveScene.add(this._resolveQuad);

    this._upscaleUniforms = { uTex: { value: null }, uTexSize: { value: new THREE.Vector2(1, 1) } };
    this._display = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), new THREE.ShaderMaterial({
      uniforms: this._upscaleUniforms,
      vertexShader: quadVertexShader,
      fragmentShader: upscaleFragmentShader,
      depthTest: false, depthWrite: false
    }));
    this._display.frustumCulled = false;
    this._display.renderOrder = -1000;
    const self = this;
    this._display.onBeforeRender = function (renderer) { self._renderVolume(renderer); };
    scene.add(this._display);

    this._time = 0;
    this._frame = 0;
    this._paused = false;
    this._orbitAngle = 0;
    this._lowSmooth = 0.5;
    this._midSmooth = 0.5;
    this._highSmooth = 0.5;
    this._dynamics = 1.0;
    this._thresholdFrom = THRESHOLD_BASE;
    this._thresholdTo = THRESHOLD_BASE;
    this._thresholdT = 1;
    this._thresholdDur = 1;
    this._lastPhraseIndex = null;
    this._lastPaletteRef = undefined;
    this._pendingDropAt = null;
    this._sweepUntil = -1;
    this._activeLights = layout.lights;
    this._placeCamera(0);
  }

  // Stretches the cloud to the frame it is actually given. The kiosk screens
  // are 1280x400 - aspect 3.2 against the 16:9 this was shaped for - and at
  // that aspect the unstretched ellipsoid subtended 18% of the half-width,
  // landing entirely inside the cover's 27% box - the whole nebula behind the
  // artwork, with
  // reading as a patch in the middle of it. The clamp keeps
  // the grown bounding sphere (max 6.4) inside CAMERA_RADIUS 7.6, so the
  // camera stays outside the volume and the entry/exit solve stays valid.
  _fitToFrame(aspect) {
    const wide = Math.min(Math.max(aspect / (16 / 9), 1), 1.8);
    if (wide === this._wide) return;
    this._wide = wide;
    const half = this._uniforms.uShapeHalf.value;
    // The envelope has to stay inside the camera's orbit or the entry/exit
    // solve is invalid - and SHAPE_HALF is bigger now, so on the widest
    // frames the aspect stretch alone would carry it past CAMERA_RADIUS.
    const maxHalf = (CAMERA_RADIUS * 0.84) / (BOUND_MARGIN * SHAPE_SCALE_MAX);
    // x AND z, so the orbit never turns the wide axis towards the camera and
    // leaves a narrow cloud half the time.
    half.set(Math.min(SHAPE_HALF[0] * wide, maxHalf), SHAPE_HALF[1],
             Math.min(SHAPE_HALF[2] * wide, maxHalf));
    // The bound has to contain the LARGEST state the envelope breathes to.
    this._uniforms.uBoundHalf.value.copy(half)
      .multiplyScalar(BOUND_MARGIN * SHAPE_SCALE_MAX);
    const mean = (half.x + half.y + half.z) / 3;
    this._uniforms.uLightReach.value = mean * 0.75;
    this._uniforms.uShadowReach.value = mean * 0.50;
  }

  _placeCamera(dt) {
    this._orbitAngle += (dt / ORBIT_PERIOD_S) * Math.PI * 2;
    const c = this._camera;
    c.position.set(
      Math.sin(this._orbitAngle) * CAMERA_RADIUS,
      CAMERA_HEIGHT,
      Math.cos(this._orbitAngle) * CAMERA_RADIUS
    );
    c.lookAt(0, 0, 0);
  }

  _renderVolume(renderer) {
    this._buffer.ensureSize(renderer);
    if (this._history.ensureSize(renderer)) this._historyFresh = true;
    this._upscaleUniforms.uTexSize.value.set(this._history.width, this._history.height);
    if (this._paused) {
      this._upscaleUniforms.uTex.value = this._history.read.texture;
      return;
    }
    // The ray basis comes from the camera's REAL world matrix, so
    // scene-manager's drift is simply part of the view rather than something
    // to fight.
    const cam = this._camera;
    cam.updateMatrixWorld();
    const e = cam.matrixWorld.elements;
    this._uniforms.uCamRight.value.set(e[0], e[1], e[2]);
    this._uniforms.uCamUp.value.set(e[4], e[5], e[6]);
    this._uniforms.uCamFwd.value.set(-e[8], -e[9], -e[10]);
    this._uniforms.uCamPos.value.copy(cam.position);
    this._uniforms.uTanHalf.value = Math.tan((cam.fov || 68) * Math.PI / 360);
    const aspect = this._buffer.width / Math.max(1, this._buffer.height);
    this._uniforms.uAspect.value = aspect;
    this._fitToFrame(aspect);



    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(this._buffer.read);
    renderer.render(this._volScene, this._ortho);
    // Into the history. The first frame after a (re)allocation takes the new
    // frame whole, so nothing fades in from black.
    const ru = this._resolveUniforms;
    ru.uCur.value = this._buffer.read.texture;
    ru.uTexel.value.set(1 / this._buffer.width, 1 / this._buffer.height);
    ru.uPrev.value = this._history.read.texture;
    const sweeping = this._time < this._sweepUntil;
    const tau = sweeping ? ACCUM_TAU_SWEEP_S : ACCUM_TAU_S;
    ru.uWeight.value = this._historyFresh || !this._accumulate ? 1
      : 1 - Math.exp(-Math.max(this._lastDt || 1 / 60, 1 / 240) / tau);
    this._historyFresh = false;
    renderer.setRenderTarget(this._history.write);
    renderer.render(this._resolveScene, this._ortho);
    this._history.swap();
    renderer.setRenderTarget(prev);
    this._upscaleUniforms.uTex.value = this._history.read.texture;
  }

  update(deltaTime, profile) {
    this._paused = profile.playbackState !== 'PLAYING';
    if (this._paused) return;
    this._time += deltaTime;
    this._lastDt = deltaTime;
    this._frame = (this._frame + 1) % 1024;
    this._uniforms.uFrame.value = this._frame;
    this._uniforms.uTime.value = this._time;
    this._placeCamera(deltaTime);

    const director = profile.director;
    const bands = director && director.bands;
    const k = 1 - Math.exp(-deltaTime / 0.5);   // slow: a cloud has inertia
    const low = bands && bands.low != null ? clamp01(bands.low) : 0.5;
    const mid = bands && bands.mid != null ? clamp01(bands.mid) : 0.5;
    const high = bands && bands.high != null ? clamp01(bands.high) : 0.5;
    this._lowSmooth += (low - this._lowSmooth) * k;
    this._midSmooth += (mid - this._midSmooth) * k;
    this._highSmooth += (high - this._highSmooth) * k;
    const character = director && director.character;
    this._dynamics = lerp(0.6, 1.3, clamp01(character && character.dynamics != null ? character.dynamics : 0.5));

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      const vivid = profile.artworkPalette && profile.artworkPalette.vividColors;
      if (vivid && vivid.length) {
        for (let i = 0; i < 3; i++) this._lightBase[i].set(vivid[i % vivid.length]);
        this._uniforms.uAmbient.value.set(vivid[vivid.length - 1]).multiplyScalar(0.05);
        this._uniforms.uBgTint.value.set(vivid[0]).multiplyScalar(0.55);
      }
    }

    // THE VOLUME EVOLVES, IT DOES NOT TRAVEL. The drift is a bounded sine on
    // three incommensurable axes, so structures are born and die in place -
    // and, as a second benefit, positions never grow, which is what keeps the
    // noise hash inside fp16's usable range on the television.
    const ds = 1.0 + this._midSmooth * 0.8;
    this._uniforms.uDrift.value.set(
      Math.sin(this._time * 0.017 * ds) * 3.0,
      Math.cos(this._time * 0.013 * ds) * 3.0,
      Math.sin(this._time * 0.0109 * ds) * 3.0
    );

    // THE ENVELOPE BREATHES. A slow sine on its own period, incommensurable
    // with the drift, plus bands.low - so the cloud opens out to its full
    // extent and draws back to a knot over about forty seconds, and the music
    // pushes it either way. This is the size variation the piece had none of.
    const breathe = 0.5 + 0.5 * Math.sin(this._time * (Math.PI * 2 / SHAPE_PERIOD_S));
    const openness = clamp01(breathe * 0.62 + this._lowSmooth * 0.38);
    this._uniforms.uShapeInner.value =
      SHAPE_INNER_KNOT + (SHAPE_INNER_HAZE - SHAPE_INNER_KNOT) * openness;
    const shapeScale =
      SHAPE_SCALE_MIN + (SHAPE_SCALE_MAX - SHAPE_SCALE_MIN) * openness;
    this._uniforms.uShapeScale.value = shapeScale;
    // and the envelope's arms turn, slowly, so the silhouette is never the
    // same shape twice.
    this._uniforms.uShapeSeed.value = this._time * SHAPE_WOBBLE_DRIFT;

    // A smaller cloud has less matter along the ray, so it also goes DIM -
    // measured, the total light more than halved across the breath, which
    // reads as fading out rather than as drawing in. Compensated, so the
    // small state is a dense bright knot and the large one an open haze.
    const sizeComp = Math.pow(SHAPE_SCALE_MAX / shapeScale, 1.15);
    this._uniforms.uDensityScale.value = DENSITY_BASE * sizeComp
      * (1 + (this._lowSmooth - 0.5) * 2 * DENSITY_BAND * this._dynamics);
    const lightGain = 1 + (this._highSmooth - 0.5) * 2 * LIGHT_BAND * this._dynamics;

    // progress -> two sources at the edges of the track, three in the middle.
    const progress = profile.duration > 0 ? clamp01(profile.progress) : 0.5;
    const wantLights = this._layout.lights >= 3 && Math.sin(Math.PI * progress) > 0.45 ? 3 : Math.min(2, this._layout.lights);
    this._activeLights = wantLights;
    this._uniforms.uLightCount.value = wantLights;

    // The sources drift inside the volume on their own slow, unequal orbits.
    const t = this._time;
    const sweeping = t < this._sweepUntil;
    // Placed in the ellipsoid's OWN space, so they spread through whatever
    // size the cloud has been stretched to instead of huddling at its core.
    const half = this._uniforms.uShapeHalf.value;
    // Scaled with the envelope: a source left at the old radius while the
    // cloud draws in would end up outside the matter it is supposed to be
    // buried in, which is the one thing this piece cannot have.
    const sc = this._uniforms.uShapeScale.value;
    for (let i = 0; i < 3; i++) {
      const s = 0.11 + i * 0.043;
      const r = (0.34 + i * 0.13) * sc;
      this._lightPos[i].set(
        Math.sin(t * s + i * 2.1) * r * half.x,
        Math.cos(t * s * 0.83 + i * 1.3) * r * 0.6 * half.y,
        Math.cos(t * s * 1.17 + i * 0.7) * r * half.z
      );
    }
    // A known drop sends ONE source straight across the volume, lighting
    // everything it passes. Rare and strong, per the brief - and it is the
    // only fast thing this piece ever does.
    if (sweeping) {
      const u = 1 - (this._sweepUntil - t) / SWEEP_S;
      const hx = this._uniforms.uShapeHalf.value.x * this._uniforms.uShapeScale.value;
      this._lightPos[0].set(lerp(-hx, hx, u), Math.sin(u * Math.PI) * 0.8, 0);
    }
    const nextEvent = director && director.nextEvent;
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 100;
    if (nextEvent && nextEvent.type === 'drop') {
      const until = nextEvent.msUntil / 1000;
      if (until > 0 && until < 2.0) this._pendingDropAt = t + until;
    }
    if (this._pendingDropAt != null && t >= this._pendingDropAt) {
      this._pendingDropAt = null;
      this._sweepUntil = t + SWEEP_S;
    }

    // Intensity rides on bands.high, applied to a COPY of the palette colour
    // so repeated frames never compound it.
    for (let i = 0; i < 3; i++) {
      this._lightColor[i].copy(this._lightBase[i]).multiplyScalar(lightGain);
    }

    // phrase -> the threshold moves, interpolated over two beats: the cloud
    // hollows out or thickens, revealing the sources buried in it.
    const phraseIndex = profile.phraseIndex;
    if (phraseIndex != null && this._lastPhraseIndex != null && phraseIndex !== this._lastPhraseIndex) {
      this._thresholdFrom = this._uniforms.uThreshold.value;
      this._thresholdTo = THRESHOLD_BASE
        + (Math.random() * 2 - 1) * THRESHOLD_SWING * this._dynamics;
      this._thresholdT = 0;
      this._thresholdDur = Math.max(0.5, PHRASE_LERP_BEATS * 60 / bpm);
    }
    this._lastPhraseIndex = phraseIndex;
    if (this._thresholdT < 1) {
      this._thresholdT = Math.min(1, this._thresholdT + deltaTime / this._thresholdDur);
      this._uniforms.uThreshold.value = lerp(this._thresholdFrom, this._thresholdTo, this._thresholdT);
    }
  }

  dispose() {
    this._scene.remove(this._display);
    this._display.geometry.dispose();
    this._display.material.dispose();
    this._volQuad.geometry.dispose();
    // NOT material.dispose(): the program it holds costs 4.7s to rebuild on
    // the Pi and is shared with every future nebula of the same layout.
    this._volQuad.material = null;
    this._buffer.dispose();
    this._history.dispose();
    this._resolveQuad.geometry.dispose();
    this._resolveQuad.material.dispose();
  }
}

